import { z } from "zod";
import { TRANSLATION_LANGS, type TranslationLang } from "./constants";

// Subscription states and what they allow (LOT 6, owner 2026-10-23). Prices and the trial are approved (owner,
// 2026-10-27, PLAN_TERMS); payments approved 2026-10-27 (CERERE nr. 7) with GRACE_DAYS after the free days. Without
// a paid (or free) period the public menu closes too (owner, 2026-10-28: „meniul se închide iar QR-ul nu va fi
// funcțional… dacă nu plătește”); nothing is deleted, paying again reopens it at once.

/**
 * The approved commercial terms, WITHOUT VAT; 30 days free on both plans. Shown everywhere in euro (owner,
 * 2026-10-27 and 2026-10-28: „prețul în euro pe site și în platformă”), in euro cents: 19.90 € / month; 99 € the
 * first year, then 149 € / year. Customers from Romania are charged in lei, and see the lei amount only at
 * checkout (owner, 2026-10-28; BNR Regulation 4/2005 art. 3: residents pay each other in lei), in bani: 107 lei
 * / month; 529 lei the first year, then 798 lei / year. Migrations 0024 and 0035 store the same values in `plans`
 * (a test keeps them equal).
 */
export const PLAN_TERMS = {
  currency: "EUR",
  vatIncluded: false,
  // Packages (owner 2026-11-04): 14 free days (was 30), monthly Standard 14.90 € / 80 lei (was 19.90 € / 107 lei).
  trialDays: 14,
  month: { id: "standard_month", priceMinor: 1490 },
  year: { id: "standard_year", introPriceMinor: 9900, priceMinor: 14900 },
  ron: { monthMinor: 8000, yearIntroMinor: 52900, yearMinor: 79800 },
} as const;

/**
 * Pro (owner 2026-11-04): everything in Standard plus the nutrition values (PRO_NUTRITION), WITHOUT free days —
 * paid from the first day. 24.90 € / month; 179 € the first year, then 229 € / year (+ VAT). In lei at the 5.35
 * rate: 133 lei / month; 956 lei the first year (the same 269 lei off as Standard), then 1,225 lei / year.
 */
export const PRO_TERMS = {
  trialDays: 0,
  month: { id: "pro_month", priceMinor: 2490, ronMinor: 13300 },
  year: { id: "pro_year", introPriceMinor: 17900, priceMinor: 22900, ronIntroMinor: 95600, ronMinor: 122500 },
  productName: "Meniova Pro",
} as const;

/** „14 zile”, „30 de zile”: Romanian puts „de” before numbers from 20 on (and their compounds). */
export const daysText = (n: number) => (n % 100 >= 1 && n % 100 <= 19 ? `${n} zile` : `${n} de zile`);

export const isProPlan = (planId: string | null | undefined) => !!planId?.startsWith("pro_");

/**
 * Nutrition analyses included (owner 2026-11-04): Pro gets 1,000 a year, of which at most 300 label readings; the
 * free days of Standard get 20, to try them on the restaurant's own dishes. One analysis = the recipe of one
 * product proposed from its name, description and ingredients (re-analysing counts again); one label reading = the
 * photos of one bought product read once.
 */
export const PRO_NUTRITION = { analyses: 1000, labels: 300 } as const;
export const TRIAL_NUTRITION = { analyses: 20, labels: 20 } as const;

/**
 * Further restaurants of an account (owner 2026-11-04): no free days, 7.90 € / month or 39 € / year + VAT; in lei
 * 42 / 209 lei + VAT (owner's choice at the 5.35 rate). Migration 0053 stores the same values in `plans`.
 */
export const EXTRA_TERMS = {
  month: { id: "extra_month", priceMinor: 790, ronMinor: 4200 },
  year: { id: "extra_year", priceMinor: 3900, ronMinor: 20900 },
  /** The Stripe line's product name. */
  productName: "Meniova – restaurant suplimentar",
} as const;

/**
 * Free plan (owner 2026-11-04): up to 20 products and 1,000 openings of the public menu a month, no AI; past the
 * openings the menu stays open for GRACE more days, then closes until the next month or a paid plan.
 */
export const FREE_TERMS = { id: "free", products: 20, scans: 1000, graceDays: 7 } as const;

/**
 * Further locations of a restaurant (owner 2026-11-04): 2.90 € / month or 19 € / year + VAT; in lei 16 / 102 + VAT
 * (owner's choice at the 5.35 rate). Free during the free days; on a running paid subscription the months left until
 * renewal are charged at once (locationChargeNow), then the location renews with the subscription.
 */
export const LOCATION_TERMS = {
  eur: { monthMinor: 290, yearMinor: 1900 },
  ron: { monthMinor: 1600, yearMinor: 10200 },
  /** The Stripe line's product name; the webhook counts the locations on an invoice by it. */
  productName: "Meniova – locație suplimentară",
} as const;

const MONTH_MS = 30.4375 * 86_400_000;

/**
 * What adding a location costs today on a running paid subscription (owner 2026-11-04: „dacă mai are 11 luni până la
 * reînnoire… facturăm 11, prețul pe lună raportat la prețul pe an împărțit la 12”): the whole months left until the
 * period ends, a begun month counted, at the monthly price, or the yearly one / 12. Without VAT.
 */
export function locationChargeNow(
  interval: "month" | "year",
  currency: "EUR" | "RON",
  periodEnd: number,
  now: number,
): { months: number; amountMinor: number } {
  const months = Math.min(
    interval === "year" ? 12 : 1,
    Math.max(1, Math.ceil(Math.max(0, periodEnd - now) / MONTH_MS)),
  );
  const p = currency === "RON" ? LOCATION_TERMS.ron : LOCATION_TERMS.eur;
  const amountMinor = interval === "month" ? p.monthMinor * months : Math.round((p.yearMinor * months) / 12);
  return { months, amountMinor };
}

/**
 * Translation languages (owner 2026-11-02, DEC-107): the menu's language and one translation are included; each
 * more costs 0.50 € a month on the monthly plan or 5 € a year on the yearly plan, + VAT, billed with the
 * subscription once the 30 free days are over. Lei at the owner's rate 5.35 (rounded to the ban), as the add-on.
 */
export const LANGUAGE_TERMS = {
  freeTranslations: 1,
  eur: { monthMinor: 50, yearMinor: 500 },
  ron: { monthMinor: 268, yearMinor: 2675 },
  /** The Stripe line's product name; the webhook counts the languages on an invoice by it. */
  productName: "Meniova – limbă suplimentară",
} as const;

/** What the onboarding wizard stores (each part optional: every step can be skipped). */
export const setupChoicesSchema = z.object({
  translationLangs: z.array(z.enum(TRANSLATION_LANGS)).max(TRANSLATION_LANGS.length).optional(),
  currency: z.enum(["RON", "EUR"]).optional(),
  planChoice: z.enum(["standard_month", "standard_year"]).nullable().optional(),
});
export type SetupChoices = z.infer<typeof setupChoicesSchema>;

export interface SetupChoicesDto {
  translationLangs: TranslationLang[];
  currency: "RON" | "EUR";
  planChoice: "standard_month" | "standard_year" | null;
  extraLanguages: number;
}

/** Extra (paid) languages for a choice of translation languages. */
export const extraLanguages = (translations: readonly string[]): number =>
  Math.max(0, new Set(translations).size - LANGUAGE_TERMS.freeTranslations);

/** The currency a restaurant is charged in: lei in Romania (and before its billing details exist), else euro. */
export const billingCurrency = (country: string | null | undefined): "RON" | "EUR" =>
  !country || country === "RO" ? "RON" : "EUR";

export type SubscriptionStatus = "beta" | "trialing" | "active" | "past_due" | "canceled" | "expired";

export interface SubscriptionState {
  status: SubscriptionStatus;
  trialEndsAt: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
}

export const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  beta: "Acces gratuit",
  trialing: "Perioadă de probă",
  active: "Abonament activ",
  past_due: "Plată restantă",
  canceled: "Abonament anulat",
  expired: "Abonament expirat",
};

/** A restaurant without a subscription row is in the free beta. */
export const BETA: SubscriptionState = {
  status: "beta",
  trialEndsAt: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
};

/**
 * What the restaurant may do now: publish changes and keep its public menu (and QR code) open to guests — the same
 * rule for both (owner, 2026-10-28). Data is never deleted: a menu closed for lack of payment reopens at once.
 */
export function entitlement(
  s: SubscriptionState,
  now: number,
): { canPublish: boolean; reason: string | null } {
  switch (s.status) {
    case "beta":
    case "active":
      return { canPublish: true, reason: null };
    case "past_due":
      // Grace while the provider retries the payment (its own retry schedule); the owner decides the policy.
      return { canPublish: true, reason: null };
    case "trialing":
      // GRACE_DAYS after the free days end, to choose a plan without losing a day of work (owner, CERERE nr. 7).
      return s.trialEndsAt !== null && now < s.trialEndsAt + GRACE_DAYS * 86_400_000
        ? { canPublish: true, reason: null }
        : {
            canPublish: false,
            reason: `Perioada de probă s-a încheiat. ${CLOSED_TEXT}`,
          };
    case "canceled":
      return s.currentPeriodEnd !== null && now < s.currentPeriodEnd
        ? { canPublish: true, reason: null }
        : { canPublish: false, reason: `Abonamentul a fost anulat. ${CLOSED_TEXT}` };
    case "expired":
      // A further restaurant starts here, never paid (owner 2026-11-04: the free days are for the first one only).
      return s.currentPeriodEnd === null && s.trialEndsAt === null
        ? { canPublish: false, reason: `Restaurantul nu are încă abonament. ${CLOSED_TEXT}` }
        : { canPublish: false, reason: `Abonamentul a expirat. ${CLOSED_TEXT}` };
  }
}

/** What a closed restaurant is told (app, e-mails). */
export const CLOSED_TEXT =
  "Meniul și codul QR sunt închise pentru clienți până alegi un abonament. Datele rămân salvate, iar după plată meniul se redeschide imediat.";

/** Whether guests can open the public menu now: the free beta, a paid or retrying subscription, the free days. */
export const menuOnline = (s: SubscriptionState, now: number) => entitlement(s, now).canPublish;

/** Stripe subscription statuses → ours. */
export function fromStripeStatus(status: string): SubscriptionStatus {
  switch (status) {
    case "trialing":
      return "trialing";
    case "active":
      return "active";
    case "past_due":
    case "unpaid":
    case "incomplete":
      return "past_due";
    case "canceled":
      return "canceled";
    default:
      return "expired"; // incomplete_expired, paused, unknown
  }
}

/** Days after the trial ends during which the restaurant can still publish while it picks a plan. */
export const GRACE_DAYS = 7;

/**
 * Owner, 2026-10-28: a restaurant may cancel any time before a payment, and ask for its money back in full within
 * REFUND_DAYS of any payment, without giving a reason. Shown on the billing page, at checkout, in the reminders
 * and on the website.
 */
export const REFUND_DAYS = 14;
export const GUARANTEE_TEXT = `Poți anula oricând înainte de plată. După fiecare plată ai ${REFUND_DAYS} zile să ceri banii înapoi, integral, fără explicații.`;

/** EU member states (ISO 3166-1 alpha-2): Romania is invoiced with VAT, the others by reverse charge. */
export const EU_COUNTRIES = [
  "RO",
  "AT",
  "BE",
  "BG",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GR",
  "HR",
  "HU",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PL",
  "PT",
  "SE",
  "SI",
  "SK",
] as const;

/** Romanian tax code (CUI/CIF) control digit: weights 753217532 over the number without its last digit. */
export function validCui(input: string): boolean {
  const digits = input.replace(/^RO/i, "");
  if (!/^\d{2,10}$/.test(digits)) return false;
  const body = digits.slice(0, -1).padStart(9, "0");
  const sum = [...body].reduce((s, d, i) => s + Number(d) * Number("753217532"[i]), 0);
  const control = ((sum * 10) % 11) % 10;
  return control === Number(digits.at(-1));
}

/** The tax code e-Factura expects for a consumer without a CNP. */
export const NO_CNP = "0000000000000";

/**
 * The restaurant's billing details. A company: Romania with a valid CUI (in Bucharest the locality is the sector,
 * as e-Factura requires), other EU countries only VAT-registered (reverse charge). A natural person (owner,
 * 2026-10-28): Romania only, name and address; the CNP is optional (13 zeros without it).
 */
export const billingProfileSchema = z
  .object({
    kind: z.enum(["company", "person"]).default("company"),
    companyName: z.string().trim().min(2, "Adaugă numele.").max(200, "Numele este prea lung."),
    vatCode: z
      .string()
      .trim()
      .toUpperCase()
      .transform((v) => v.replace(/\s/g, ""))
      .default(""),
    vatPayer: z.boolean().default(false),
    regCom: z
      .string()
      .trim()
      .max(40, "Numărul de înregistrare este prea lung.")
      .optional()
      .transform((v) => v || undefined),
    address: z.string().trim().min(3, "Adaugă adresa.").max(300, "Adresa este prea lungă."),
    city: z.string().trim().min(2, "Adaugă localitatea.").max(100, "Localitatea este prea lungă."),
    county: z.string().trim().min(2, "Adaugă județul.").max(100, "Județul este prea lung."),
    country: z.enum(EU_COUNTRIES, "Facturăm în Uniunea Europeană."),
    email: z
      .string()
      .trim()
      .toLowerCase()
      .max(254, "Adresa de e-mail este prea lungă.")
      .pipe(z.email("Adaugă o adresă de e-mail validă.")),
  })
  .superRefine((p, ctx) => {
    const county = p.county.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
    if (p.country === "RO" && county === "bucuresti" && !/^sector [1-6]$/i.test(p.city))
      ctx.addIssue({
        code: "custom",
        path: ["city"],
        message: "În București, localitatea este sectorul: Sector 1 … Sector 6.",
      });
    if (p.kind === "person") {
      if (p.country !== "RO")
        ctx.addIssue({
          code: "custom",
          path: ["country"],
          message: "Facturăm persoane fizice doar din România.",
        });
      if (p.vatCode && !/^\d{13}$/.test(p.vatCode))
        ctx.addIssue({
          code: "custom",
          path: ["vatCode"],
          message: "CNP-ul are 13 cifre (sau lasă câmpul gol).",
        });
      return;
    }
    if (!/^[A-Z0-9]{2,20}$/.test(p.vatCode))
      ctx.addIssue({ code: "custom", path: ["vatCode"], message: "Codul fiscal are doar litere și cifre." });
    else if (p.country === "RO" && !validCui(p.vatCode))
      ctx.addIssue({ code: "custom", path: ["vatCode"], message: "CUI-ul nu este valid." });
    if (p.country !== "RO" && !p.vatPayer)
      ctx.addIssue({
        code: "custom",
        path: ["vatPayer"],
        message: "Pentru firme din alte țări UE facturăm doar cu cod de TVA valid (taxare inversă).",
      });
  })
  .transform((p) =>
    p.kind === "person" ? { ...p, vatCode: p.vatCode || NO_CNP, vatPayer: false, regCom: undefined } : p,
  );
export type BillingProfileInput = z.infer<typeof billingProfileSchema>;
